import { useEffect, useRef, useState, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { useAuthStore } from "../stores/authStore";
import { message, notification } from "antd";

export interface EWBAlert {
  eWayBillId: number;
  eWayBillNo: string;
  alertType: string;
  message: string | null;
  severityLevel: "info" | "warning" | "error" | "critical";
  occurredAt: string;
}

const SEVERITY_ICONS: Record<string, string> = {
  info: "ℹ️",
  warning: "⚠️",
  error: "❌",
  critical: "🚨",
};

export function useEWBNotifications(onAlert?: (alert: EWBAlert) => void) {
  const [connected, setConnected] = useState(false);
  const [alerts, setAlerts] = useState<EWBAlert[]>([]);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const token = useAuthStore((s) => s.accessToken);

  const clearAlerts = useCallback(() => setAlerts([]), []);

  useEffect(() => {
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl("/hubs/transport", {
        accessTokenFactory: () => token,
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets |
                   signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("ReceiveEWBAlert", (alert: EWBAlert) => {
      setAlerts((prev) => [alert, ...prev].slice(0, 50)); // keep last 50

      const icon = SEVERITY_ICONS[alert.severityLevel] ?? "📋";
      const isCritical = alert.severityLevel === "critical" || alert.severityLevel === "error";

      if (isCritical) {
        notification.open({
          key: `ewb-${alert.eWayBillId}-${Date.now()}`,
          message: `${icon} E-Way Bill Alert`,
          description: `EWB ${alert.eWayBillNo}: ${alert.message ?? alert.alertType}`,
          duration: 0, // critical stays until dismissed
          type: alert.severityLevel === "critical" ? "error" : "warning",
          placement: "topRight",
        });
      } else {
        message.open({
          type: alert.severityLevel === "info" ? "info" : "warning",
          content: `${icon} EWB ${alert.eWayBillNo}: ${alert.message ?? alert.alertType}`,
          duration: 4,
        });
      }

      onAlert?.(alert);
    });

    connection.onreconnecting(() => setConnected(false));
    connection.onreconnected(() => setConnected(true));
    connection.onclose(() => setConnected(false));

    void connection.start()
      .then(() => setConnected(true))
      .catch((err) => console.warn("EWB SignalR connect failed:", err));

    connectionRef.current = connection;

    return () => {
      void connection.stop();
      connectionRef.current = null;
      setConnected(false);
    };
  }, [token, onAlert]);

  return { connected, alerts, clearAlerts };
}
